import { useRouteMatch } from "react-router-dom";
import { useEffect, useState } from "react";

import Loader from "@syntensor/common/components/loader";
import ErrorComp from "@syntensor/common/components/error";
import Button from "@syntensor/common/components/button";
import Input from "@syntensor/common/components/input";
import getCopy from "@syntensor/common/data/copy";
import { EStudyType } from "@syntensor/common/types";

import { IStudyMatchParams } from "../types";
import CELL_LINES_AVAILABLE from "./cell_lines_available";
import useCreateStudyForm from "./use_create_study_form";
import { getAllAvailableEndpointsForStudyType } from "./endpoints";
import { COMPONENT_TYPES } from "./component_types_form";
import CreateNewStudyCompoundsForm from "./compounds";
import {
  popCompoundConfig,
  potencyCompoundConfig,
  potencyGeneConfig,
  potencyPathwayConfig,
} from "./compounds/tableConfig";

import styles from "./create.module.css";
import { navigateTo } from "@syntensor/common/browser_history";
import { TInputChange } from "@syntensor/common/hooks/use_form_input";
import RadioButtonsForm from "./radio_buttons_form";

export default function CreateNewStudyForm() {
  const [isFormValid, setFormValid] = useState(false);
  const { params } = useRouteMatch<IStudyMatchParams>();
  const { projectId, studyId } = params;

  const backUrl = `/projects/${projectId}`;

  const initialState = {
    cellLine: CELL_LINES_AVAILABLE[1],
    type: null,
    componentTypes: COMPONENT_TYPES.map((type) => type.id),
  };

  const {
    inputs,
    handleInputChange,
    handleCompoundChange,
    isTemplateLoading,
    templateLoadError,
    onSubmit,
    isFormLoading,
    errorMsg,
  } = useCreateStudyForm({ initialState });

  useEffect(() => {
    setFormValid(inputs.type != null);
  }, [inputs]);

  const setStudyType = (type: EStudyType) => {
    const endpoints = getAllAvailableEndpointsForStudyType(type);
    const name = "type";

    handleInputChange([
      { name, value: type },
      { name: "endpoints", value: endpoints },
    ]);

    setFormValid(true);
  };

  const back = () => {
    if (studyId === undefined && inputs.type) {
      const name = "type";
      handleInputChange([
        { name, value: null },
        { name: "endpoints", value: [] },
      ]);

      setFormValid(true);
    } else {
      navigateTo(backUrl);
    }
  };

  if (isTemplateLoading) {
    return <Loader />;
  }
  if (templateLoadError) {
    return <ErrorComp error={templateLoadError} />;
  }

  const typeNames: Record<EStudyType, string> = {
    [EStudyType.PO_PROGRESSION]: "/ Probability of Progression",
    [EStudyType.POTENCY]: "/ Potency",
    [EStudyType.TOXICITY]: "/ ADMETox",
    [EStudyType.MOA_DECONVOLUTION]: "/ Mechanism of Action deconvolution",
  };

  return (
    <div id="root" className={styles.newUserForm}>
      <form onSubmit={onSubmit}>
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}>
            {getCopy("projects_project_configure")}{" "}
            {typeNames[inputs.type as EStudyType]}
          </h2>

          <div className={styles.buttonsWrapper}>
            <Button role="subtle" onClick={back}>
              {inputs.type && <>{getCopy("studies_search_discard-changes")}</>}
              {!inputs.type && <>{getCopy("studies_search_cancel")}</>}
            </Button>
          </div>
        </div>

        <input type="hidden" value={inputs.type || ""} />

        {!inputs.type && (
          <div className={styles.content}>
            <div className={styles.typeGroup}>
              <h2 className={styles.subtitle}>
                {getCopy("studies_type_select-type")}
              </h2>
            </div>

            <div className={styles.typeGroup}>
              <div>{getCopy("studies_type_clinical-outcomes")}</div>

              <div className={styles.typeWrapper}>
                <Button
                  size="extralarge"
                  role="subtle"
                  onClick={() => {
                    setStudyType(EStudyType.PO_PROGRESSION);
                  }}
                >
                  {getCopy("studies_type_po-progression")}
                </Button>
                <p>{getCopy("studies_description_po-progression")}</p>
              </div>
            </div>

            <div className={styles.typeGroup}>
              <div>Candidate Selection</div>
              <div className={styles.typeColumn}>
                <div className={styles.typeWrapper}>
                  <Button
                    size="extralarge"
                    role="subtle"
                    onClick={() => {
                      setStudyType(EStudyType.POTENCY);
                    }}
                  >
                    {getCopy("studies_type_potency-button")}
                  </Button>
                  <p>{getCopy("studies_description_potency")}</p>
                </div>

                <div className={styles.typeWrapper}>
                  <Button
                    size="extralarge"
                    role="subtle"
                    onClick={() => {
                      setStudyType(EStudyType.TOXICITY);
                    }}
                  >
                    {getCopy("projects_menu_admetox")}
                  </Button>
                  <p>{getCopy("studies_description_admetox")}</p>
                </div>
              </div>
            </div>

            <div className={styles.typeGroup}>
              <div>Hypothesis Validation</div>

              <div className={styles.typeWrapper}>
                <Button
                  size="extralarge"
                  role="subtle"
                  onClick={() => {
                    setStudyType(EStudyType.MOA_DECONVOLUTION);
                  }}
                >
                  {getCopy("studies_type_moa-deconvolution")}
                </Button>
                <p>{getCopy("studies_description_moa")}</p>
              </div>
            </div>
          </div>
        )}

        {inputs.type && (
          <div className={styles.content}>
            {inputs.type === EStudyType.PO_PROGRESSION && (
              <CreateNewStudyCompoundsForm
                title={getCopy("studies_create_compounds")}
                editTitle="Compounds"
                editButtonTitle={"Edit compounds"}
                compounds={inputs.compounds}
                onChange={handleCompoundChange}
                tableConfig={popCompoundConfig}
              />
            )}

            {inputs.type === EStudyType.POTENCY && (
              <>
                <RadioButtonsForm
                  title={getCopy("studies_create_select-cell-line-disease")}
                  name="cellLine"
                  values={[
                    { id: "A375", name: "Malignant melanoma (A375)" },
                    {
                      id: "A549",
                      name: "Non-small cell lung carcinoma (A549)",
                    },
                    { id: "HA1E", name: "Renal carcinoma (HA1E) " },
                    { id: "HT29", name: "Colon cancer (HT29)" },
                    { id: "MCF7", name: "Breast cancer (MCF7)" },
                    { id: "PC3", name: "Prostate cancer (PC3)" },
                  ]}
                  value={inputs.cellLine}
                  onChange={handleInputChange}
                />

                <CreateNewStudyCompoundsForm
                  title={getCopy("studies_create_potencycompounds")}
                  editButtonTitle={"Edit compounds"}
                  editTitle={"Compounds"}
                  compounds={inputs.compounds}
                  onChange={handleCompoundChange}
                  tableConfig={potencyCompoundConfig}
                />

                <CreateNewStudyCompoundsForm
                  title={getCopy("studies_create_gene")}
                  editButtonTitle={"Edit genes"}
                  editTitle={"Genes"}
                  compounds={inputs.targetExternalComponents}
                  onChange={(e: any) => {
                    handleInputChange([
                      {
                        name: "targetExternalComponents",
                        value: e,
                      },
                    ]);
                  }}
                  tableConfig={potencyGeneConfig}
                />

                <CreateNewStudyCompoundsForm
                  title={getCopy("studies_create_pathway")}
                  editButtonTitle={"Edit pathways"}
                  editTitle={"Pathways"}
                  compounds={inputs.targetExternalPathways}
                  onChange={(e: any) => {
                    handleInputChange([
                      {
                        name: "targetExternalPathways",
                        value: e,
                      },
                    ]);
                  }}
                  tableConfig={potencyPathwayConfig}
                />
              </>
            )}

            {inputs.type === EStudyType.TOXICITY && (
              <>
                <CreateNewStudyCompoundsForm
                  title={getCopy("studies_create_potencycompounds")}
                  editButtonTitle={"Edit compounds"}
                  editTitle={"Compounds"}
                  compounds={inputs.compounds}
                  onChange={handleCompoundChange}
                  tableConfig={potencyCompoundConfig}
                />

                <CreateNewStudyCompoundsForm
                  title={getCopy("studies_create_gene")}
                  editButtonTitle={"Edit genes"}
                  editTitle={"Genes"}
                  compounds={inputs.targetExternalComponents}
                  onChange={(e: any) => {
                    handleInputChange([
                      {
                        name: "targetExternalComponents",
                        value: e,
                      },
                    ]);
                  }}
                  tableConfig={potencyGeneConfig}
                />

                <CreateNewStudyCompoundsForm
                  title={getCopy("studies_create_pathway")}
                  editButtonTitle={"Edit pathways"}
                  editTitle={"Pathways"}
                  compounds={inputs.targetExternalPathways}
                  onChange={(e: any) => {
                    handleInputChange([
                      {
                        name: "targetExternalPathways",
                        value: e,
                      },
                    ]);
                  }}
                  tableConfig={potencyPathwayConfig}
                />
              </>
            )}

            {inputs.type === EStudyType.MOA_DECONVOLUTION && (
              <>
                <RadioButtonsForm
                  title={getCopy("studies_create_select-cell-line-disease")}
                  name="cellLine"
                  values={[
                    { id: "A375", name: "Malignant melanoma (A375)" },
                    {
                      id: "A549",
                      name: "Non-small cell lung carcinoma (A549)",
                    },
                    {
                      id: "A1E",
                      name: "TERT immortalized kidney epithelial (A1E)",
                    },
                    { id: "HELA", name: "Cervical cancer epithelial (HELA)" },
                    { id: "HEPG2", name: "Hepatocellular carcinoma (HEPG2)" },
                    { id: "HT29", name: "Colon cancer epithelial (HT29)" },
                    {
                      id: "MCF10A",
                      name: "Non-tumorigenic breast epithelial (MCF10A)",
                    },
                    { id: "MCF7", name: "Breast cancer epithelial (MCF7)" },
                    { id: "PC3", name: "Metastatic prostate cancer (PC3)" },
                    { id: "VCAP", name: "Prostate cancer epithelial (VCAP)" },
                    { id: "YAPC", name: "Pancreatic carcinoma (YAPC)" },
                  ]}
                  value={inputs.cellLine}
                  onChange={function (evt: TInputChange): void {
                    handleInputChange(evt);
                  }}
                />

                <CreateNewStudyCompoundsForm
                  title={getCopy("studies_create_potencycompounds")}
                  editButtonTitle={"Edit compounds"}
                  editTitle={"Compounds"}
                  compounds={inputs.compounds}
                  onChange={handleCompoundChange}
                  tableConfig={potencyCompoundConfig}
                />

                <CreateNewStudyCompoundsForm
                  title={getCopy("studies_create_gene")}
                  editButtonTitle={"Edit genes"}
                  editTitle={"Genes"}
                  compounds={inputs.targetExternalComponents}
                  onChange={(e: any) => {
                    handleInputChange([
                      {
                        name: "targetExternalComponents",
                        value: e,
                      },
                    ]);
                  }}
                  tableConfig={potencyGeneConfig}
                />

                <CreateNewStudyCompoundsForm
                  title={getCopy("studies_create_pathway")}
                  editButtonTitle={"Edit pathways"}
                  editTitle={"Pathways"}
                  compounds={inputs.targetExternalPathways}
                  onChange={(e: any) => {
                    handleInputChange([
                      {
                        name: "targetExternalPathways",
                        value: e,
                      },
                    ]);
                  }}
                  tableConfig={potencyPathwayConfig}
                />
              </>
            )}

            <div className={styles.formSection}>
              <h3 className={styles.formTitle}>
                {getCopy("studies_create_name")}
              </h3>
              <Input
                placeholder={getCopy("studies_create_name-placeholder")}
                name="displayName"
                defaultValue={inputs.displayName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className={styles.formSection}>
              <h3 className={styles.formTitle}>
                {getCopy("studies_create_description")}
              </h3>
              <Input
                placeholder={getCopy("studies_create_description-placeholder")}
                name="description"
                elName="textarea"
                rows={5}
                defaultValue={inputs.description}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className={styles.formSection}>
              <Button
                isDisabled={
                  isFormLoading || !isFormValid || studyId !== undefined
                }
                role="primary"
                type="submit"
              >
                {isFormLoading && <Loader size="32px" />}
                {getCopy("studies_buttons_submit")}
              </Button>

              {errorMsg && <div className={styles.error}>{errorMsg}</div>}
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
